import React from 'react';
import {ValueCell} from '../../../../../../components/initial-plan/ValueCell';
import {HIDDEN_FEATURES} from '../../../../../../constants';
import ForecastTooltip from '../../../../../shared/components/tooltips/ForecastTooltip';
import ProjectUtil from '../../../../../shared/util/ProjectUtil';
import {getNonBillableTime} from '../../../../../shared/util/time-registration/time-registration-settings/BillableTimeSplitUtil';
import {Data, HorizontalBar, Title} from '../ProjectScopingTotals_styled';
import {EstimateValueTooltip, TimeEntriesValueTooltip} from '../DeprecatedProjectScopingTotalsTooltips';
import {AvailabilityTile} from './AvailabilityTile';
import {Tile, TileRow} from './Tile_styled';
import {isFeatureHidden} from '../../../../../shared/util/CompanySetupUtil';
import {useProjectTotals} from '../ProjectScopingTotalsContext';
import {useIntl} from 'react-intl';

interface EstimateAndTimeTileProps {
	showBillableSplit: boolean;
	showAvailabilityTile: boolean;
}

const EstimateRow: React.FC = () => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title singleLine>
				{totalsData.isEstimatedInHours
					? intl.formatMessage({id: 'project_scoping.total_estimate_in_plan'})
					: intl.formatMessage({id: 'project_scoping.total_points_estimate_in_plan'})}
			</Title>
			<Data small>
				<ForecastTooltip placement={'left'} content={EstimateValueTooltip()}>
					<ValueCell
						noJustify
						type={totalsData.isEstimatedInHours ? ValueCell.VALUE_TYPE.TIME : ValueCell.VALUE_TYPE.POINTS}
						value={totalsData.estimate}
					/>
				</ForecastTooltip>
			</Data>
		</TileRow>
	);
};

const TimeEntriesRow: React.FC = () => {
	const intl = useIntl();
	const {totalsData, projectData} = useProjectTotals();
	return (
		<TileRow>
			{!ProjectUtil.projectUsesManualProgress(projectData) && !isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) && (
				<>
					<Title singleLine>{intl.formatMessage({id: 'project_scoping.actual_hours_spent'})}</Title>
					<Data small>
						<ForecastTooltip placement={'left'} content={TimeEntriesValueTooltip()}>
							<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData.timeEntries} />
						</ForecastTooltip>
					</Data>
				</>
			)}
		</TileRow>
	);
};

const BillableTimeEntriesRow: React.FC = () => {
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title singleLine>{'Billable hours spent to date'}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData.billableTimeEntries} />
			</Data>
		</TileRow>
	);
};

const NonBillableTimeEntriesRow: React.FC = () => {
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title singleLine>{'Non-billable hours spent to date'}</Title>
			<Data small>
				<ValueCell
					noJustify
					type={ValueCell.VALUE_TYPE.TIME}
					value={getNonBillableTime(totalsData.timeEntries, totalsData.billableTimeEntries)}
				/>
			</Data>
		</TileRow>
	);
};

export const EstimateAndTimeTile: React.FC<EstimateAndTimeTileProps> = ({showBillableSplit, showAvailabilityTile}) => {
	return (
		<Tile>
			<EstimateRow />
			<TimeEntriesRow />
			{showBillableSplit ? (
				<>
					<HorizontalBar transparent />
					<BillableTimeEntriesRow />
					<NonBillableTimeEntriesRow />
				</>
			) : showAvailabilityTile ? (
				<>
					<HorizontalBar />
					<AvailabilityTile />
				</>
			) : null}
		</Tile>
	);
};
